import React, { useState } from 'react'
import { CollapsibleSpan } from '../PipelineInformationModal.js'
import { openModal } from '../../../../../Flux/Slice/ModalSlice/ModalSlice.js'
import { useGetAuthenticatedUser } from '../../../../../Flux/Selector/index.js'
import { useDispatch } from 'react-redux'
import { deriveItemState } from './DeriveItemState.js'
import type { FileInProcessing, IOrder } from '../../../../../Model/index.js'
import { isEdnaProcessingType, isOtuProcessingType } from '../../../../../Util/processingType.ts'
import Actions from '../../../../../Flux/index.js'
import { orderSlice } from '../../../../../Flux/Slice/Order/OrderSlice.js'
import { isPairedEndInput } from '../../../../../Model/Order/processingProfile.ts'

interface PreprocessingTabProps {
  poolItems: FileInProcessing[]
  order: IOrder
  showCheckboxes: boolean
  setShowCheckboxes: (showCheckboxes: boolean) => void
  onClose: () => void
}

export const PreprocessingTab = (props: PreprocessingTabProps) => {
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [checkboxState, setCheckboxState] = useState<Record<string, boolean>>({})
  const user = useGetAuthenticatedUser()
  const dispatch = useDispatch()

  const isPairedEnd = isPairedEndInput(props.order)

  const handleShowCheckboxes = () => {
    const initialBoxes: Record<string, boolean> = {}
    props.poolItems.forEach((i) => {
      initialBoxes[i.id] = !i.pipelineInformation?.errorAt && (i.pipelineInformation?.sequenceCountAfterQualityFiltered || -1) > 0
    })
    setCheckboxState(initialBoxes)
    props.setShowCheckboxes(true)
  }

  const handleForceSubmit = () => {
    const excludedItems = props.poolItems.filter((i) => !checkboxState[i.id]).map((i) => i.id)
    props.onClose()
    dispatch(openModal({ modalType: 'startPostprocessing', props: { order: props.order, excludedItems } }))
  }

  return (
    <>
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by Customer Sample Name"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input input-bordered w-full"
        />
      </div>

      {/* Table for displaying pipeline information */}
      <div className="overflow-x-auto">
        <table className="table table-zebra w-full">
          <thead>
            <tr>
              {props.showCheckboxes && <th></th>}
              <th></th>
              <th>ID</th>
              <th>Name</th>
              <th>Fwd Primer</th>
              <th>Rev Primer</th>
              <th>Total Sequences</th>
              <th>Merged Sequences</th>
              <th>Forward Trimmed</th>
              <th>Reverse Trimmed</th>
              <th>Quality Filtered</th>
              {props.poolItems.find(
                (x) => x?.pipelineInformation?.dereplicateTotalSequenceLength || x?.pipelineInformation?.dereplicateUniqueSequenceLength
              ) && (
                <>
                  <th>Derep Total Seq</th>
                  <th>Derep Unique Seq</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {props.poolItems
              .filter(
                (item) =>
                  !item.customerSampleName || !searchQuery || item.customerSampleName.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map((item) => (
                <tr key={item.id}>
                  {props.showCheckboxes && (
                    <td>
                      <input
                        className="checkbox"
                        disabled={
                          !!item.pipelineInformation?.errorAt || (item.pipelineInformation?.sequenceCountAfterQualityFiltered ?? 0) == 0
                        }
                        checked={!!checkboxState[item.id]}
                        type="checkbox"
                        onChange={() => {
                          setCheckboxState((previous) => {
                            return { ...previous, [item.id]: !previous[item.id] }
                          })
                        }}
                      ></input>
                    </td>
                  )}
                  <td>{deriveItemState(item.pipelineInformation)}</td>
                  <td>
                    <CollapsibleSpan maxChars={5} content={item.id} />
                  </td>
                  <td>{item.customerSampleName}</td>
                  <td>{item.forwardPrimer && <CollapsibleSpan maxChars={5} content={item.forwardPrimer} />}</td>
                  <td>{item.reversePrimer && <CollapsibleSpan maxChars={5} content={item.reversePrimer} />}</td>
                  <td>
                    {
                      /* a pair counts once in the stored total */
                      item.pipelineInformation?.sequenceCountTotal
                        ? item.pipelineInformation.sequenceCountTotal * (isPairedEnd ? 2 : 1)
                        : 'N/A'
                    }
                  </td>
                  <td>{item.pipelineInformation?.sequenceCountMerged ?? 'N/A'}</td>
                  <td>{item.pipelineInformation?.sequenceCountForwardTrimmed ?? 'N/A'}</td>
                  <td>{item.pipelineInformation?.sequenceCountReverseTrimmed ?? 'N/A'}</td>
                  <td>{item.pipelineInformation?.sequenceCountAfterQualityFiltered ?? 'N/A'}</td>
                  {props.poolItems.find(
                    (x) => x?.pipelineInformation?.dereplicateTotalSequenceLength || x?.pipelineInformation?.dereplicateUniqueSequenceLength
                  ) && (
                    <>
                      <td>{item.pipelineInformation?.dereplicateTotalSequenceLength ?? 'N/A'}</td>
                      <td>{item.pipelineInformation?.dereplicateUniqueSequenceLength ?? 'N/A'}</td>
                    </>
                  )}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="modal-action">
        {props.order.orderDone && isOtuProcessingType(props.order.processingType) && (
          <button className="btn btn-outline" onClick={() => window.open(`/order/${props.order.id}/print-report`, '_blank')}>
            Printable Preview
          </button>
        )}
        {props.order.orderDone && isEdnaProcessingType(props.order.processingType) && (
          <button
            className="btn btn-outline"
            onClick={() =>
              dispatch(Actions[orderSlice.name].downloadOrderArtifactRequest({ orderId: props.order.id, artifact: 'PROCESSING_SUMMARY' }))
            }
          >
            Report Summary
          </button>
        )}
        {props.order.orderPreprocessed &&
          !props.order.orderPostprocessingStarted &&
          props.order.lab == user?.org?.id &&
          /* at least one item was insufficient or in error */
          props.poolItems.find(
            (i) => i.pipelineInformation?.errorAt || (!i.pipelineInformation?.qualityVerifiedAt && i.pipelineInformation?.completedAt)
          ) &&
          /* all are in error or completed */
          !props.poolItems.find((i) => !i.pipelineInformation?.completedAt && !i.pipelineInformation?.errorAt) &&
          (props.showCheckboxes ? (
            <button
              className="btn outline-solid outline-red-700"
              onClick={handleForceSubmit}
              disabled={!Object.values(checkboxState).find((s) => s)}
            >
              Confirm Force Submit
            </button>
          ) : (
            <button className="btn outline-solid" onClick={handleShowCheckboxes}>
              Force Submit To Postprocessing
            </button>
          ))}
        <button className="btn btn-primary" onClick={props.onClose}>
          Close
        </button>
      </div>
    </>
  )
}
